// FinalReflection — 4 universal questions shown when the buyer finishes
// an edition. Saves to /api/library/reflection/{slug}. Designed to feel
// like the closing pages of a magazine — a takeaway worksheet, not a quiz.
import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const QUESTIONS = [
  { key: "headline",  label: "Your headline takeaway",
    prompt: "If this edition were a single line you'd remember in five years, what would it be?",
    placeholder: "One sentence." },
  { key: "move",      label: "The move you'll make this week",
    prompt: "What is the ONE specific action you'll take in the next 7 days because of this read?",
    placeholder: "Be specific — what, when, with whom." },
  { key: "block",     label: "What's in the way",
    prompt: "What's the most likely thing to stop you from making that move? Name it on the page.",
    placeholder: "The honest answer." },
  { key: "signal",    label: "How you'll know it worked",
    prompt: "What's the small signal — in 14 days — that tells you the move landed?",
    placeholder: "A measurable, observable thing." },
];

export default function FinalReflection({ slug, isLightMode, onContinue }) {
  const [answers, setAnswers] = useState({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Pre-load existing answers
  useEffect(() => {
    axios.get(`${API}/api/library/reflection/${slug}`, { withCredentials: true })
      .then((r) => setAnswers(r.data?.answers || {}))
      .catch(() => {});
  }, [slug]);

  const setA = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/library/reflection/${slug}`,
        { answers },
        { withCredentials: true }
      );
      setSaved(true);
      toast.success("Your reflection is saved to your shelf.");
    } catch {
      toast.error("Couldn't save just now. Your answers stay on this device.");
    }
    setSaving(false);
  };

  const filledCount = QUESTIONS.filter((q) => (answers[q.key] || "").trim().length > 0).length;

  return (
    <div className="text-left mt-14 w-full max-w-2xl mx-auto" data-testid="final-reflection">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-cream/70" />
        <p className="text-[10px] tracking-[0.3em] uppercase text-cream/65">
          The closing pages · your reflection
        </p>
      </div>
      <h3 className="font-serif italic text-3xl md:text-4xl mb-3 text-cream">
        Before you close this issue.
      </h3>
      <p className="text-cream/65 text-sm leading-relaxed mb-8 max-w-lg">
        Four questions. Two minutes. The pages you finish without writing back rarely change you — these will.
      </p>

      <div className="space-y-6">
        {QUESTIONS.map((q, i) => (
          <div key={q.key} className="border-l border-cream/15 pl-5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-cream/55 mb-1">
              {String(i + 1).padStart(2, "0")} · {q.label}
            </div>
            <p className="font-serif italic text-cream text-lg leading-snug mb-3">
              {q.prompt}
            </p>
            <textarea
              value={answers[q.key] || ""}
              onChange={(e) => setA(q.key, e.target.value)}
              placeholder={q.placeholder}
              rows={2}
              data-testid={`reflection-${q.key}`}
              className="w-full bg-cream/5 border border-cream/15 focus:outline-none focus:border-cream/40 p-3 text-sm leading-relaxed text-cream font-serif"
            />
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap gap-3 items-center justify-between">
        <span className="text-[11px] text-cream/55 tracking-[0.25em] uppercase">
          {filledCount} of {QUESTIONS.length} filled
        </span>
        <div className="flex gap-3">
          <button
            onClick={save}
            disabled={saving || filledCount === 0}
            data-testid="reflection-save"
            className="inline-flex items-center gap-2 bg-cream text-ink px-6 py-3 overline tracking-[0.28em] hover:bg-cream/90 disabled:opacity-40 transition-colors"
          >
            {saved ? <><Check className="h-4 w-4"/> Saved</> : (saving ? "Saving…" : "Save to my shelf")}
          </button>
          <button
            onClick={onContinue}
            data-testid="reflection-skip"
            className="overline tracking-[0.28em] text-cream/60 hover:text-cream"
          >
            Skip — next reads ↓
          </button>
        </div>
      </div>
    </div>
  );
}
